import {storeToRefs} from 'pinia';
import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
import {
	GsoaProductCatalogEshopService,
	type Gsoa_ProductCatalog_Eshop_GetProductCategoriesEshop_response as ProductCategories,
	type Gsoa_ProductCatalog_Eshop_ProductCategoryEshop as ProductCategory,
	type Gsoa_ProductCatalog_Eshop_GetBrands_response as Brands,
	GsoaProductCatalogEnumsService,
	type Gsoa_ProductCatalog_Enums_GetCountries_response as Countries,
	type Gsoa_ProductCatalog_Enums_Country as CountryEnum,
} from '~/openapi';
import type {Tree} from '~/types/utils';
import {ROOT_PRODUCT_CATEGORY_ID, KONTOBARIERY, MYGLOBUSPRICES} from '#shared/constants';
import {createTreeFromChildren} from '~/utils';

// Virtual categories for full catalog
export const fullCatalogVirtualCategories = {
	myGlobusPrices: {
		categoryId: MYGLOBUSPRICES.id,
		categoryName: MYGLOBUSPRICES.label,
		isActive: true,
		isHidden: false,
	},
	kontoBariery: {
		categoryId: KONTOBARIERY.id,
		categoryName: KONTOBARIERY.label,
		isActive: true,
		isHidden: false,
	},
} as const satisfies Record<string, Partial<ProductCategory>>;

export const isFullCatalogCategoryIdVirtual = (categoryId?: string | null): boolean => {
	if (!categoryId) {
		return false;
	}
	return Object.values(fullCatalogVirtualCategories).some((cat) => cat.categoryId === categoryId);
};

type CountryGroupDiscriminator = (country: CountryEnum) => boolean;

const neighboringCountryIds = ['AT', 'DE', 'PL', 'SK'];

const europeanCountryIds = [
	'AD', 'AL', 'AT', 'BA', 'BE', 'BG', 'BY', 'CH', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FO', 'FR', 'GB', 'GG',
	'GI', 'GR', 'HR', 'HU', 'IE', 'IM', 'IS', 'IT', 'JE', 'LI', 'LT', 'LU', 'LV', 'MC', 'MD', 'ME', 'MK', 'MT', 'NL',
	'NO', 'PL', 'PT', 'RO', 'RS', 'RU', 'SE', 'SI', 'SJ', 'SK', 'SM', 'TR', 'UA', 'VA', 'XK',
];

const countryGroupDiscriminators: CountryGroupDiscriminator[] = [
	({id}) => id === 'CZ',
	({id}) => neighboringCountryIds.includes(id!),
	({id}) => europeanCountryIds.includes(id!),
	() => true,
];

function getCountrySortGroup(country: CountryEnum): number {
	return countryGroupDiscriminators.findIndex((discriminator) => discriminator(country));
}

function sortCountries(countries: CountryEnum[]): CountryEnum[] {
	// First sort by country group, then by name
	return countries.sort((a, b) => {
		const aSortGroup = getCountrySortGroup(a);
		const bSortGroup = getCountrySortGroup(b);

		return aSortGroup - bSortGroup || a.name!.localeCompare(b.name!);
	});
}

export const useProductCatalogStore = defineStore('productCatalog', () => {
	const categories = ref<ProductCategories | null>(null);
	const countries = ref<Countries | null>(null);
	const actionCountries = ref<Countries | null>(null);
	const brands = ref<Brands | null>(null);
	const actionBrands = ref<Brands | null>(null);

	const countriesCount = ref<number | null>(null);
	const actionCountriesCount = ref<number | null>(null);
	const brandsCount = ref<number | null>(null);
	const actionBrandsCount = ref<number | null>(null);

	const ENUMS_PAGE_SIZE = 2500;

	const categoryTree = computed<Tree<ProductCategory> | null>(() => (
		createTreeFromChildren(
			categories.value?.productCategoriesEshop?.filter((c) => c.isActive && !c.isHidden) ?? [],
			'categoryId',
			'childrenIds',
		).find(({item}) => item?.categoryId === ROOT_PRODUCT_CATEGORY_ID) ?? null
	));

	// Mirrors the backend `getMainCategories` BFS (GsoaProductCatalogDataService): starting from
	// the root's children, an active and non-hidden node is a main category, otherwise we descend
	// into its children to find active descendants. Order matters for `resolveMainCategory`.
	const mainCategories = computed<Map<string, ProductCategory>>(() => {
		const result = new Map<string, ProductCategory>();
		const allCategories = categories.value?.productCategoriesEshop;
		if (!allCategories?.length) {
			return result;
		}

		const byId = new Map<string, ProductCategory>(allCategories.map((c) => [c.categoryId, c]));
		const root = byId.get(ROOT_PRODUCT_CATEGORY_ID);
		if (!root) {
			return result;
		}

		const queue = [...(root.childrenIds ?? [])];
		while (queue.length > 0) {
			const candidateId = queue.shift()!;
			const candidate = byId.get(candidateId);
			if (!candidate) {
				continue;
			}
			if (candidate.isActive && !candidate.isHidden) {
				result.set(candidate.categoryId, candidate);
			} else {
				queue.push(...(candidate.childrenIds ?? []));
			}
		}

		return result;
	});

	function resolveMainCategory(categoryIds?: Array<string> | null): ProductCategory | null {
		if (!categoryIds?.length) {
			return null;
		}
		const candidateIds = new Set(categoryIds);
		for (const [categoryId, category] of mainCategories.value) {
			if (candidateIds.has(categoryId)) {
				return category;
			}
		}
		return null;
	}

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarketWithFallbackInstance} = storeToRefs(hypermarketsStore);
	const houseNumberWithFallback = computed(() => selectedHypermarketWithFallbackInstance.value?.content.gsoaId);

	async function fetchCategories() {
		if (categories.value !== null) {
			return categories.value;
		}

		try {
			categories.value = await cancelTimeout(() => (
				GsoaProductCatalogEshopService.getEshopProductCategories({pageSize: ENUMS_PAGE_SIZE})
			));
			return categories.value ?? null;
		} catch (e: any) {
			throw createError({
				message: 'Failed to fetch product categories',
				cause: e,
			});
		}
	}

	async function fetchBrands() {
		await fetchBrandsGeneric(brands, brandsCount, {listedProductsOnly: true});
	}

	async function fetchActionBrands() {
		await fetchBrandsGeneric(actionBrands, actionBrandsCount, {actionProducts: true});
	}

	async function fetchCountries() {
		await fetchCountriesGeneric(countries, countriesCount, {listedProductsOnly: true});
	}

	async function fetchActionCountries() {
		await fetchCountriesGeneric(actionCountries, actionCountriesCount, {actionProducts: true});
	}

	async function fetchBrandsGeneric(
		targetRef: Ref<Brands | null>,
		targetCountRef: Ref<number | null>,
		options: {actionProducts?: boolean; products?: boolean; listedProductsOnly?: boolean} = {},
	) {
		const errorMsg = `Failed to fetch ${options.actionProducts ? 'action ' : ''}brands`;
		await fetchGeneric(
			targetRef,
			targetCountRef,
			() => GsoaProductCatalogEshopService.getEshopBrands({pageSize: ENUMS_PAGE_SIZE, houseNumber: houseNumberWithFallback.value, ...options}),
			errorMsg,
		);
	}

	async function fetchCountriesGeneric(
		targetRef: Ref<Countries | null>,
		targetCountRef: Ref<number | null>,
		options: {actionProducts?: boolean; products?: boolean; listedProductsOnly?: boolean} = {},
	) {
		const errorMsg = `Failed to fetch ${options.actionProducts ? 'action ' : ''}countries`;
		await fetchGeneric(
			targetRef,
			targetCountRef,
			() => GsoaProductCatalogEnumsService.getEnumsCountries({pageSize: ENUMS_PAGE_SIZE, houseNumber: houseNumberWithFallback.value, ...options}),
			errorMsg,
			(response) => response.countries ? {countries: sortCountries(response.countries)} : null,
		);
	}

	async function fetchGeneric<T>(
		targetRef: Ref<T | null>,
		targetCountRef: Ref<number | null>,
		apiCall: () => Promise<T>,
		errorMessage: string,
		transform?: (data: T) => T | null,
	) {
		try {
			const response = await cancelTimeout(apiCall);
			const transformed = transform ? transform(response) : response;
			targetRef.value = transformed ?? null;
			targetCountRef.value = (response as T & {totalCount?: number}).totalCount ?? null;
		} catch (e: any) {
			throw createError({
				message: errorMessage,
				cause: e,
			});
		}
	}

	return {
		categories,
		categoryTree,
		mainCategories,
		resolveMainCategory,
		fetchCategories,
		countries,
		actionCountries,
		fetchCountries,
		fetchActionCountries,
		brands,
		actionBrands,
		fetchBrands,
		fetchActionBrands,
		countriesCount,
		actionCountriesCount,
		brandsCount,
		actionBrandsCount,
	};
});
